<template>
    <div class="grid xl:grid-cols-2 2xl:grid-cols-3 gap-8 mt-4">
        <div v-for="productPrice in tierProducts" :key="productPrice" class="rounded border flex flex-col">
            <div class="pl-3 pr-3 pt-3 text-sm font-bold">
                {{ getFromDictionary(`products.${productPrice.product.productType}`, 'label').toLocaleUpperCase() }}
            </div>

            <div class="px-3 mt-1 pb-3">
                <div class="pb-3 text-sm min-h-[5rem]">
                    {{ getFromDictionary(`products.${productPrice.product.productType}`, 'description') }}
                </div>
                <div v-if="productPrice.product.productType !== 'trial'">
                    <template v-if="productPrice.product.productType === 'enterprise'">
                        Custom Pricing
                    </template>
                    <template v-else>
                        <span class="font-bold">{{
                            $filters.formatCurrency(productPrice.price?.amount)
                        }}</span>/{{ getPriceIntervalName(productPrice.price) }}
                    </template>
                </div>
            </div>

            <div class="border-b mx-3"></div>

            <div class="pl-3 pr-3 pt-3 flex-grow">
                <div class="pb-3 text-xs font-bold">WHAT'S INCLUDED</div>
                <div class="pb-4">
                    <ul class="space-y-2.5">
                        <li
                            v-for="includedItem in getIncludedItems(productPrice.product)"
                            :key="includedItem"
                            class="flex text-sm leading-none"
                        >
                            <AppIcon name="check" class="my-auto h-3 w-3 text-emerald-500" />
                            <span class="pl-2">{{ includedItem }}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div
                v-if="productPrice.product.productType !== 'trial' &&
                    (isSfStaff || ['admin', 'superadmin'].includes(selfActiveOrganization?.organization_operator?.role))"
                class="flex justify-center p-4"
            >
                <template
                    v-if="!hasScheduledChanges &&
                        productPrice.price.selfManaged &&
                        productPrice?.price?.relatedPriceId &&
                        !activeSubscription.hasEndingSubscription"
                >
                    <template v-if="!activeSubscription.hasSubscription">
                        <Button
                            variant="default"
                            size="lg"
                            class="w-full xl:max-w-xs font-semibold"
                            @click="onPurchase(productPrice)"
                        >
                            Subscribe
                        </Button>
                    </template>

                    <template
                        v-else-if="activeSubscription.rank <= getFromDictionary(`products.${productPrice.product.productType}`, 'rank')"
                    >
                        <Button
                            variant="default"
                            size="lg"
                            class="w-full xl:max-w-xs font-semibold"
                            @click="onChangePlan(productPrice)"
                        >
                            Upgrade
                        </Button>
                    </template>

                    <template v-else>
                        <Button
                            variant="default"
                            size="lg"
                            class="w-full xl:max-w-xs font-semibold"
                            @click="onChangePlan(productPrice)"
                        >
                            Downgrade
                        </Button>
                    </template>
                </template>
                <template v-else-if="productPrice.product.productType !== 'trial'">
                    <Button
                        variant="outline"
                        size="lg"
                        class="w-full xl:max-w-xs font-semibold"
                        @click="onContactUs(productPrice)"
                    >
                        Contact us
                    </Button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Button } from '@/components/ui/button'
import _ from 'lodash'
import { trackEvent, AnalyticalEvents } from '@/utils/analytics'

export default {
    name: 'PlanProductsTable',
    components: {
        Button,
    },
    props: {
        productPrices: {
            type: Object,
        },
        isProductsLoading: {
            type: Boolean,
        },
        activeSubscription: {
            type: Object,
        },
        hasScheduledChanges: {
            type: Boolean,
        },
    },

    emits: ['purchase', 'switch-plan'],

    data() {
        return {
            relatedProductCounts: {},
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'selfActiveOrganization', 'isSfStaff', 'selfSubscriptionTier']),
        tierProducts() {
            return _.filter(this.productPrices, (productPrice) => {
                return ['trial', 'starter', 'growth', 'scale', 'enterprise'].includes(productPrice.product.productType)
            })
        },
    },

    watch: {
        productPrices() {
            this.resetPriceCounts()
        },
    },

    created() {
        this.resetPriceCounts()
    },

    methods: {
        resetPriceCounts() {
            this.relatedProductCounts = {}
            _.forEach(this.productPrices, (productPrice) => {
                this.relatedProductCounts[productPrice.price.id] = 0
                if (productPrice.price.relatedPriceId) {
                    this.relatedProductCounts[productPrice.price.relatedPriceId] = 0
                }
            })
        },
        onPurchase(productPrice) {
            const additionalProducts = []
            if (this.relatedProductCounts[productPrice.price.relatedPriceId] > 0) {
                additionalProducts.push({
                    priceId: productPrice.price.relatedPriceId,
                    quantity: this.relatedProductCounts[productPrice.price.relatedPriceId],
                })
            }

            // Trigger subscription tier click event
            trackEvent(AnalyticalEvents.SalesSubscriptionTierSelected, {
                from_tier: this.selfSubscriptionTier ?? null,
                new_tier: productPrice.product.name ?? null,
                price: productPrice.price.amount ?? null,
            })


            this.$emit('purchase', productPrice, additionalProducts)
        },
        onChangePlan(productPrice) {
            const additionalProducts = []
            if (this.relatedProductCounts[productPrice.price.relatedPriceId] > 0) {
                additionalProducts.push({
                    priceId: productPrice.price.relatedPriceId,
                    quantity: this.relatedProductCounts[productPrice.price.relatedPriceId],
                })
            }
            this.$emit('switch-plan', productPrice, additionalProducts)
        },
        getPriceIntervalName(productPrice) {
             if (productPrice.billingInterval === 'year') {
                return 'year'
            }
            return 'mo'
        },
        getSeatPrice(id) {
            const seatPrice = _.find(this.productPrices, (productPrice) => {
                return productPrice.price.id === id
            })
            return seatPrice
        },
        getIncludedItems(product) {
            let includedItems = this.getFromDictionary(`products.${product.productType}`, 'included')
            includedItems = [...includedItems]

            const defaultSeats = this.getFromDictionary(`products.${product.productType}`, 'defaultSeats')

            if (product.productType === 'enterprise') {
                includedItems.unshift('Custom seats')
            } else if (defaultSeats > 1) {
                includedItems.unshift(`${defaultSeats} seats`)
            } else {
                includedItems.unshift('1 seat')
            }

            return includedItems
        },
        onRelatedCountChanged(relatedPriceId) {
            if (this.relatedProductCounts[relatedPriceId] < 0) {
                this.relatedProductCounts[relatedPriceId] = 0
            } else if (this.relatedProductCounts[relatedPriceId] > 999) {
                this.relatedProductCounts[relatedPriceId] = 999
            }
        },
        onContactUs() {
            // Trigger sales contact clicked event
            trackEvent(AnalyticalEvents.SalesContactClicked, {
                from_tier: this.selfSubscriptionTier,
            })

            window.open(
                'https://calendly.com/d/cqzs-9m2-9hn/build-your-custom-plan',
                '_blank',
                'noopener,noreferrer'
            )
        },
    },
}
</script>
